import React, { useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';
import { ActivityIndicator, Box, Button, Spacing, Text } from '../../ui';
import { Dialog, TextData } from '../../components';
import { useApi, useAsync } from '../../hooks';
import { correctionFieldLabel, formatCorrectionValue, formatFeedValue, lossFlag, newestEditedAt } from './correctionDiffFields';

/* Read-only explanation of a corrected Skyward loss: what the feed said when the correction was made
   vs. what we show now, and — only when the feed has since moved — which fields diverged and how.
   Fetched fresh on every open (never carried on the loss payload) so it always reflects the latest
   feed load. */
const LossCorrectionDiffDialog = ({ row, applicationId, onClose }) => {
  const api = useApi();
  const claimLineKey = row && row.claimLineKey;

  const fetchCorrections = useCallback(async () => {
    return await api.getSkywardCorrections(applicationId, claimLineKey);
  }, [api, applicationId, claimLineKey]);

  const { value, status } = useAsync(fetchCorrections, { immediate: !!claimLineKey });
  const loading = status === 'pending';
  const errored = status === 'error';
  const entries = useMemo(() => (Array.isArray(value) ? value : []), [value]);
  const divergedEntries = useMemo(() => entries.filter((entry) => entry.diverged), [entries]);
  const editedAt = useMemo(() => newestEditedAt(entries), [entries]);

  // Read straight off the clicked row rather than the fetch response: the row is what determined
  // which flag word the user actually clicked, and it's available synchronously, so the heading
  // never flickers from one word to the other while the fetch is still pending.
  const flagWord = lossFlag(row);
  const heading = row && row.claimNumber ? `${flagWord} — ${row.claimNumber}` : flagWord;

  return (
    <Dialog
      open={!!row}
      heading={heading}
      styles={{ dialogBox: { marginLeft: ({ theme }) => theme.layout.centeredOverlay.marginLeft } }}
      onClose={onClose}
      actions={<Button label="Close" onPress={onClose} />}
    >
      <Box width="100%">
        {loading ? <ActivityIndicator /> : null}
        {!loading && errored ? (
          <Text color="$error" bold small>
            Failed to load loss corrections.
          </Text>
        ) : null}
        {!loading && !errored && !entries.length ? <Text small>No corrections found for this loss.</Text> : null}
        {!loading && !errored && entries.length ? (
          <Box width="100%">
            <Text small color="$gray.500">
              Feed value when edited → our value
            </Text>
            {entries.map((entry) => (
              <TextData
                key={entry.field}
                label={correctionFieldLabel(entry.field)}
                data={`${formatFeedValue(entry.field, entry.feedValueAtEdit)} → ${formatCorrectionValue(entry.field, entry.correctedValue)}`}
              />
            ))}
          </Box>
        ) : null}
        {divergedEntries.length ? (
          <Box width="100%">
            <Spacing vertical="1.5" />
            <Text color="$error" bold small>
              The feed has changed since this correction was made:
            </Text>
            {divergedEntries.map((entry) => (
              <TextData
                key={`diverged-${entry.field}`}
                label={correctionFieldLabel(entry.field)}
                data={`Now: ${formatFeedValue(entry.field, entry.currentFeedValue)} (was: ${formatFeedValue(entry.field, entry.feedValueAtEdit)})`}
                textProps={{ color: '$error' }}
              />
            ))}
          </Box>
        ) : null}
        {!loading && !errored && editedAt ? (
          <>
            <Spacing vertical="1.5" />
            <Text small color="$gray.500">
              Edited {DateTime.fromMillis(Number(editedAt)).toFormat('MM-dd-yyyy')}
            </Text>
          </>
        ) : null}
      </Box>
    </Dialog>
  );
};

export { LossCorrectionDiffDialog };
